/**
 * NMI Collect.js singleton manager (owner Pay Bill).
 * Fixed DOM IDs: #payButton, #ccnumber, #ccexp, #cvv.
 */

declare global {
  interface Window {
    CollectJS: any;
  }
}

const COLLECT_JS_URL = "https://secure.networkmerchants.com/token/Collect.js";
const COLLECT_JS_SCRIPT_ID = "nmi-collectjs-script-owner";

let scriptPromise: Promise<void> | null = null;
let scriptTokenizationKey: string | null = null;
let activeConfigKey: string | null = null;
let latestCallback: ((response: any) => void) | null = null;

const NMI_PAY_BUTTON_ID = "payButton";
const NMI_CCNUMBER_ID = "ccnumber";
const NMI_CCEXP_ID = "ccexp";
const NMI_CVV_ID = "cvv";

function allNmiElementsExist(): boolean {
  return !!(
    document.getElementById(NMI_PAY_BUTTON_ID) &&
    document.getElementById(NMI_CCNUMBER_ID) &&
    document.getElementById(NMI_CCEXP_ID) &&
    document.getElementById(NMI_CVV_ID)
  );
}

export function loadCollectJsOnce(tokenizationKey: string): Promise<void> {
  const existingScript = document.getElementById(COLLECT_JS_SCRIPT_ID) as HTMLScriptElement | null;
  if (existingScript) {
    const existingKey = existingScript.getAttribute("data-tokenization-key");
    if (existingKey === tokenizationKey && scriptPromise) {
      return scriptPromise;
    }
    existingScript.remove();
    document.querySelectorAll('script[data-collectjs="true"]').forEach((script) => script.remove());
    if (typeof window !== "undefined" && (window as any).CollectJS) {
      delete (window as any).CollectJS;
    }
    scriptPromise = null;
    scriptTokenizationKey = null;
  }

  if (scriptPromise && scriptTokenizationKey === tokenizationKey) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = COLLECT_JS_SCRIPT_ID;
    script.src = COLLECT_JS_URL;
    script.setAttribute("data-tokenization-key", tokenizationKey);
    script.async = true;
    script.onload = () => {
      scriptTokenizationKey = tokenizationKey;
      const maxWait = 1200;
      const interval = 40;
      const started = Date.now();
      const check = () => {
        if (
          typeof window !== "undefined" &&
          (window as any).CollectJS &&
          typeof (window as any).CollectJS.configure === "function"
        ) {
          resolve();
          return;
        }
        if (Date.now() - started >= maxWait) {
          resolve();
          return;
        }
        setTimeout(check, interval);
      };
      setTimeout(check, interval);
    };
    script.onerror = (err) => {
      scriptPromise = null;
      scriptTokenizationKey = null;
      reject(err);
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export type ConfigureResult =
  | "configured"
  | "already-configured"
  | "script-not-ready"
  | "dom-not-ready";

export interface ConfigureCollectOnceParams {
  configKey: string;
  tokenizationKey: string;
  paymentSelector: string;
  fieldSelectors: {
    ccnumber: string;
    ccexp: string;
    cvv: string;
  };
  callback: (response: any) => void;
  onFieldsReady?: () => void;
  onTimeout?: () => void;
}

export function configureCollectOnce(params: ConfigureCollectOnceParams): ConfigureResult {
  latestCallback = params.callback;

  if (typeof document === "undefined" || !document.querySelector) {
    return "dom-not-ready";
  }

  if (!allNmiElementsExist()) {
    return "dom-not-ready";
  }

  if (activeConfigKey === params.configKey) {
    return "already-configured";
  }

  if (!window.CollectJS || typeof window.CollectJS.configure !== "function") {
    return "script-not-ready";
  }

  try {
    window.CollectJS.configure({
      variant: "inline",
      paymentSelector: params.paymentSelector,
      fields: {
        ccnumber: {
          selector: params.fieldSelectors.ccnumber,
          title: "Card Number",
          placeholder: "1234 5678 9012 3456",
        },
        ccexp: {
          selector: params.fieldSelectors.ccexp,
          title: "Expiration",
          placeholder: "MM / YY",
        },
        cvv: {
          display: "show",
          selector: params.fieldSelectors.cvv,
          title: "CVV",
          placeholder: "123",
        },
      },
      timeoutDuration: 10000,
      timeoutCallback: function () {
        params.onTimeout?.();
      },
      fieldsAvailableCallback: function () {
        params.onFieldsReady?.();
      },
      callback: function (response: any) {
        latestCallback?.(response);
      },
    });
    activeConfigKey = params.configKey;
    return "configured";
  } catch (error) {
    console.error("Error configuring CollectJS:", error);
    throw error;
  }
}

export function resetCollectConfig(): void {
  activeConfigKey = null;
  latestCallback = null;
}

export const NMI_IDS = {
  payButton: NMI_PAY_BUTTON_ID,
  ccnumber: NMI_CCNUMBER_ID,
  ccexp: NMI_CCEXP_ID,
  cvv: NMI_CVV_ID,
} as const;
